import { useId, useState } from 'react';
import { meetsAaContrast } from '../../tokens/contrast.ts';
import styles from './ColorField.module.css';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const TEXT_ON_COLOR = '#FFFFFF';

export type ColorFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readableHint: string;
  lowContrastHint: string;
  invalidHint: string;
  sample: string;
};

export const ColorField = ({
  label,
  value,
  onChange,
  readableHint,
  lowContrastHint,
  invalidHint,
  sample,
}: ColorFieldProps) => {
  const id = useId();
  const [draft, setDraft] = useState(value);
  const valid = HEX_COLOR.test(draft);
  const shown = valid ? draft : value;
  const readable = meetsAaContrast(TEXT_ON_COLOR, shown);
  const contrastHint = readable ? readableHint : lowContrastHint;
  const hint = valid ? contrastHint : invalidHint;
  const tone = valid && readable ? 'ok' : 'warn';

  const accept = (next: string) => {
    setDraft(next);
    if (HEX_COLOR.test(next)) onChange(next.toUpperCase());
  };

  return (
    <div className={styles['field']}>
      <label htmlFor={`${id}-hex`} className={styles['label']}>
        {label}
      </label>
      <div className={styles['row']}>
        <input
          type="color"
          className={styles['swatch']}
          value={shown.toLowerCase()}
          aria-label={label}
          onChange={(event) => {
            accept(event.target.value);
          }}
        />
        <input
          id={`${id}-hex`}
          className={styles['hex']}
          value={draft}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={!valid}
          aria-describedby={`${id}-hint`}
          onChange={(event) => {
            accept(event.target.value.trim());
          }}
        />
        <span className={styles['preview']} style={{ background: shown }} aria-hidden="true">
          {sample}
        </span>
      </div>
      <span id={`${id}-hint`} className={styles['hint']} data-tone={tone}>
        {hint}
      </span>
    </div>
  );
};
